import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";

export function useDashboardMetrics(isAdmin: boolean, isLoading: boolean) {
  const { toast } = useToast();

  return useQuery({
    queryKey: ["dashboard-metrics"],
    queryFn: async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          return { scheduled: 0, available: 0, total: 0 };
        }
        
        const d = new Date();
        const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        let scheduledQuery = supabase
          .from("letters_with_owner")
          .select("*", { count: 'exact', head: true })
          .eq("payment_status", "paid")
          .gt("delivery_date", today); // Cartas com data de entrega no futuro

        let availableQuery = supabase
          .from("letters_with_owner")
          .select("*", { count: 'exact', head: true })
          .eq("payment_status", "paid")
          .lte("delivery_date", today); // Cartas com data de entrega hoje ou no passado

        let totalQuery = supabase
          .from("letters_with_owner")
          .select("*", { count: 'exact', head: true })
          .eq("payment_status", "paid");

        if (!isAdmin) {
          scheduledQuery = scheduledQuery.eq("user_id", session.user.id);
          availableQuery = availableQuery.eq("user_id", session.user.id);
          totalQuery = totalQuery.eq("user_id", session.user.id);
        }
        
        const { count: scheduledCount, error: scheduledError } = await scheduledQuery;
        const { count: availableCount, error: availableError } = await availableQuery;
        const { count: totalCount, error: totalError } = await totalQuery;

        if (scheduledError) {
          logger.error("Erro ao contar cartas agendadas:", scheduledError);
        }
        
        if (availableError) {
          logger.error("Erro ao contar cartas disponíveis:", availableError);
        }

        if (totalError) {
          logger.error("Erro ao contar total de cartas:", totalError);
        }
        
        return {
          scheduled: scheduledCount || 0,
          available: availableCount || 0,
          total: totalCount || 0
        };
      } catch (error) {
        logger.error("Erro ao carregar métricas:", error);
        toast({
          variant: "destructive",
          title: "Erro ao carregar métricas",
          description: "Ocorreu um erro inesperado."
        });
        return { scheduled: 0, available: 0, total: 0 };
      }
    },
    enabled: !isLoading,
  });
}